import { Injectable, Inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import {
  AuthApiError,
  OktaAuth,
  TokenResponse,
  Tokens,
  toQueryString,
  TransactionStorage,
} from '@okta/okta-auth-js';
import { defer, throwError, firstValueFrom, from } from 'rxjs';
import jwt_decode from 'jwt-decode';

import {
  CoreWindowConfig,
  CORE_WINDOW_CONFIG,
  WebviewMessageBus,
  WindowService,
  IframeMessageBus,
} from '@cigna/shared/angular/core/window-util';
import { LoggingService } from '@cigna/shared/angular/logging-util';
import { Authenticator, OidcResult, TokenBody } from './interfaces';
import { AuthError } from './auth-error';
import { OktaFlowConfig, OKTA_FLOW_CONFIG } from './okta-flow-config';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';

const TOKEN_COOKIE = 'accessToken';

interface SigninWithCredentialsOptions {
  username: string;
  password: string;
  options: {
    multiOptionalFactorEnroll: boolean;
    warnBeforePasswordExpired: boolean;
  };
  stateToken?: string;
}

interface ResetPasswordOptions {
  oldPassword: string;
  newPassword: string;
  stateToken?: string;
}
@Injectable({
  providedIn: 'root',
})
export class OktaFlowService implements Authenticator {
  private _okta: OktaAuth;

  constructor(
    private _logger: LoggingService,
    @Inject(OKTA_FLOW_CONFIG) private config: OktaFlowConfig,
    @Inject(CORE_WINDOW_CONFIG) private _windowConfig: CoreWindowConfig,
    private _iframe: IframeMessageBus,
    private _http: HttpClient,
    private _window: WindowService,
    private _cookie: CookieService,
    private _webviewMessageBus: WebviewMessageBus,
  ) {
    const redirectUri = this.getUrlWithOrigin(config.redirectUri);
    const issuer = this.getUrlWithOrigin(config.issuer);

    this._okta = new OktaAuth({
      redirectUri,
      issuer,
      responseType: 'code',
      postLogoutRedirectUri: config.postLogoutRedirectUri,
      pkce: true,
      clientId: config.clientId,
      maxClockSkew:
        typeof config.clockSkew === 'undefined'
          ? Number.POSITIVE_INFINITY
          : config.clockSkew,
      tokenManager: {
        storage: this.config.tokenManager?.storage,
      },
    });
    if (config.redirectUri.startsWith('com.')) {
      this._okta.options.redirectUri = config.redirectUri;
    }

    if (config.proxy) {
      this.setupProxy(config.proxy);
    }

    if (config.debug) {
      this.logEvents();
    }

    const storage = this._okta.storageManager.getHttpCache();
    const oidcKey = `${issuer}/.well-known/openid-configuration`;
    const oidcConfig = storage.getItem(oidcKey);
    if (oidcConfig?.response?.error) {
      this._logger.warn('Wrong openid configuration found. Clearing cache...');
      storage.removeItem?.(oidcKey);
    }
  }

  authenticate(authData?: { previousAccessToken: TokenBody }) {
    if (this.isImpersonator(authData?.previousAccessToken)) {
      throw new AuthError({ error: 'login_required' });
    }
    return defer(() => this.silentSignIn());
  }

  async verifyRecoveryToken(recoveryToken: string) {
    return this._okta.verifyRecoveryToken({ recoveryToken });
  }

  async forgotPassword(username: string, factorType: string) {
    return this._okta.forgotPassword({
      username,
      factorType,
    });
  }

  resetPassword(params: ResetPasswordOptions) {
    return from(
      this._okta.authn.postToTransaction(
        `${this.getBaseUrl()}/api/v1/authn/credentials/change_password`,
        params,
      ),
    ).pipe(catchError((e) => this.handleHttpError(e)));
  }

  signInWithCredentials(params: SigninWithCredentialsOptions) {
    return from(this._okta.signInWithCredentials(params)).pipe(
      catchError((e) => this.handleHttpError(e)),
    );
  }

  async silentSignIn(sessionToken?: string): Promise<OidcResult> {
    if (this._windowConfig.isWebview) {
      // authentication in device
      if (this.canGetTokensFromNative()) {
        return this.getTokensFromNative();
      }

      // authentication in browser, using cached accessToken
      if (this.config.cookieStorage) {
        try {
          // checking if user has an active session already
          const auth = this.getInitialData();
          if (auth) {
            this.storeTokensInSession({
              accessToken: {
                accessToken: auth.accessToken,
              },
            });
            return auth;
          }
        } catch {
          /* ignore the default, Router-based redirect as webview may live in another app */
        }
        this.setDeepLink();
        this._window.location.assign(this.config.postLogoutRedirectUri);
      }
    }
    if (this._windowConfig.isEmbedded) {
      return this.getTokensFromHost();
    }
    try {
      const { tokens } = await this._okta.token.getWithoutPrompt({
        scopes: ['openid'],
        sessionToken,
      });

      return this.tokensToOidc(tokens);
    } catch (e) {
      if (e.errorCode === 'login_required') {
        this.setDeepLink();
        throw new AuthError({ error: e.errorCode, message: e.message });
      }

      this._logger.error(e, 'error initiating okta auth flow');

      throw new AuthError(e);
    }
  }

  signOut(
    params?: Record<string, string>,
    redirectUrl: string = this.config.postLogoutRedirectUri,
  ) {
    this.deleteAccessTokenCookie();

    const redirectUrlWithParams = params
      ? redirectUrl.concat(toQueryString(params))
      : redirectUrl;
    return this._okta
      .signOut({ postLogoutRedirectUri: redirectUrlWithParams })
      .catch(() => {
        this._window.location.href = redirectUrlWithParams;
      });
  }

  async parseTokensFromUrl(options: { responseMode: 'fragment' | 'query' }) {
    const parseFromUrl: (options: {
      responseMode: string;
    }) => Promise<TokenResponse> = this._okta.token.parseFromUrl;
    return parseFromUrl(options).then(({ tokens }) => tokens);
  }

  introspect(stateToken: string) {
    return from(this._okta.authn.introspect({ stateToken })).pipe(
      catchError((e) => this.handleHttpError(e)),
    );
  }

  isImpersonator(token?: TokenBody) {
    return Array.isArray(token?.scp) && token?.scp.includes('impersonation');
  }

  getTransactionStorage(): TransactionStorage<any> {
    return this._okta.storageManager.getTransactionStorage();
  }

  setOriginalUri(uri: string) {
    this._okta.setOriginalUri(uri);
  }

  getOriginalUri(): string | undefined {
    return this._okta.getOriginalUri();
  }

  getUrlWithOrigin(url: string): string {
    return url.startsWith('http') || url.startsWith('com.')
      ? url
      : `${this._window.location.origin}${url}`;
  }

  getProxyUrl(proxyUri: string): string {
    return this.getUrlWithOrigin(proxyUri).replace(/\/$/, '');
  }

  getBaseUrl(): string {
    return this.config.proxy
      ? this.getProxyUrl(this.config.proxy)
      : this._okta.getIssuerOrigin();
  }

  setupProxy(proxy: string): void {
    const fetchRequest = this._okta.options.httpRequestClient;

    if (fetchRequest && this._okta.options.issuer) {
      const proxyUrl = this.getProxyUrl(proxy);
      const proxyOrigin = getOrigin(proxyUrl);
      const issuerOrigin = getOrigin(this._okta.options.issuer);

      this._okta.getIssuerOrigin = () => proxyOrigin;
      this._okta.options.httpRequestClient = (method, url, opts) => {
        const localUrl = url.replace('https://localhost:4200', issuerOrigin);
        return fetchRequest(
          method,
          localUrl.replace(issuerOrigin, proxyUrl),
          opts,
        );
      };
      this._okta.options.authorizeUrl = `${proxyUrl}/oauth2/default/v1/authorize`;
      this._okta.session.close = () =>
        firstValueFrom(
          this._http.delete(`${proxyUrl}/api/v1/sessions/me`, {
            withCredentials: true,
          }),
        );
    }
  }

  federate() {
    return this._okta.token.getWithRedirect({
      scopes: ['openid'],
    });
  }

  setCookieStorage(accessToken: string) {
    if (this.config.cookieStorage) {
      this._cookie.set(
        TOKEN_COOKIE,
        accessToken,
        new Date(Date.now() + 10 * 60 * 1000), // +10 minutes
        '/',
        this.getCookieDomain(),
        true,
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  storeTokensInSession(tokens: any) {
    if (this.config.tokenManager && tokens?.accessToken) {
      const existingTokens = sessionStorage.getItem('okta-token-storage');
      if (existingTokens) {
        const parsedTokens = JSON.parse(existingTokens);
        if (
          parsedTokens?.accessToken?.accessToken ===
          tokens.accessToken.accessToken
        ) {
          return;
        }
      }

      const expiryTime = Date.now() + 10 * 60 * 1000; // 10 minutes, same as cookie

      const tokensWithExpiry = {
        accessToken: {
          ...tokens.accessToken,
          expiresAt: expiryTime / 1000,
        },
        idToken: tokens.idToken
          ? {
              ...tokens.idToken,
              expiresAt: expiryTime / 1000,
            }
          : undefined,
      };

      this._okta.tokenManager.setTokens(tokensWithExpiry);
    }
  }

  getInitialData() {
    if (this.config.cookieStorage) {
      const accessToken = this._cookie.get(TOKEN_COOKIE);
      if (accessToken) {
        try {
          const auth = tokenToOidc(accessToken);
          if (auth) {
            this.storeTokensInSession({
              accessToken: {
                accessToken: auth.accessToken,
              },
            });
          }

          return auth;
        } catch (e) {
          this._logger.warn(e);
          this.deleteAccessTokenCookie();
        }
      }
      throw new AuthError({ error: 'login_required' });
    }
  }

  getCookieDomain() {
    return undefined;
  }

  deleteAccessTokenCookie() {
    if (this.config.cookieStorage) {
      this._cookie.delete(TOKEN_COOKIE, '/', this.getCookieDomain());
    }
    this.deleteSessionStorageTokens();
  }

  deleteSessionStorageTokens() {
    if (this.config.tokenManager) {
      this._okta.tokenManager.clear();
    }
  }

  logEvents() {
    const originalFetchRequest = this._okta.options.httpRequestClient;
    const logger = this._logger;
    if (originalFetchRequest) {
      this._okta.options.httpRequestClient = function (method, url, options) {
        return originalFetchRequest(method, url, options)
          .then((res) => {
            logger.info(
              `OKTA SDK: method ${method}, url: ${url} headers: ${JSON.stringify(
                options.headers,
              )} result: ${JSON.stringify(res)}`,
            );
            return res;
          })
          .catch((err) => {
            logger.info(
              `OKTA SDK: method ${method}, url: ${url} headers: ${JSON.stringify(
                options.headers,
              )} error: ${JSON.stringify(err)}`,
            );
            throw err;
          });
      };
    }
  }

  canGetTokensFromNative(): boolean {
    return (
      !!this.config.retrieveTokensFromNative &&
      this._webviewMessageBus.hasRetrieveAccessTokenMethod()
    );
  }

  getTokensFromNative() {
    this._logger.info('calling the app to get the tokens');

    this._webviewMessageBus.getTokens();
    return new Promise<OidcResult>((resolve, reject) => {
      const listener = (event: MessageEvent) => {
        this._logger.info('received a message', event.data);
        const data = event.data;
        if (typeof data !== 'object') {
          return;
        }
        if ('webviewAccessToken' in data) {
          this._logger.info(
            'received a message with webviewAccessToken',
            data.webviewAccessToken,
          );

          try {
            const oidcResult = tokenToOidc(data.webviewAccessToken);
            this.setCookieStorage(data.webviewAccessToken);
            this.storeTokensInSession({
              accessToken: {
                accessToken: data.webviewAccessToken,
              },
            });
            resolve(oidcResult);
          } catch (e) {
            reject(e);
          }
          this._window.removeEventListener('message', listener);
        } else if ('webviewError' in data) {
          this._logger.info(
            'received a message with webviewError',
            data.webviewError,
          );

          reject(data.webviewError);
          this._window.removeEventListener('message', listener);
        }
      };

      this._window.addEventListener('message', listener);
    });
  }

  getTokensFromHost() {
    this._logger.info('calling the host to get the tokens');
    this._iframe.requestToken();
    return new Promise<OidcResult>((resolve, reject) => {
      const listener = (event: MessageEvent) => {
        this._logger.info('received a message', event.data);
        const data = event.data;
        if (typeof data !== 'object') {
          return;
        }
        if ('hostAccessToken' in data) {
          this._logger.info(
            'received a message with hostAccessToken',
            data.hostAccessToken,
          );

          try {
            const oidcResult = tokenToOidc(data.hostAccessToken);
            resolve(oidcResult);
          } catch (e) {
            reject(e);
          }
          this._window.removeEventListener('message', listener);
        } else if ('hostError' in data) {
          this._logger.info(
            'received a message with hostError',
            data.hostError,
          );

          reject(data.hostError);
          this._window.removeEventListener('message', listener);
        }
      };

      this._window.addEventListener('message', listener);
    });
  }

  async handleCallback() {
    if (this._okta.token.isLoginRedirect()) {
      const { tokens } = await this._okta.token.parseFromUrl();
      return this.tokensToOidc(tokens);
    }
  }

  tokensToOidc(tokens: Tokens) {
    if (!tokens.accessToken || !checkClaims(tokens.accessToken.claims)) {
      throw Error(`Missing access token`);
    }

    if (!tokens.idToken || !checkClaims(tokens.idToken.claims)) {
      throw Error(`Missing id token`);
    }

    this.setCookieStorage(tokens.accessToken.accessToken);
    this.storeTokensInSession(tokens);

    return {
      accessToken: tokens.accessToken.accessToken,
      accessTokenBody: tokens.accessToken.claims,
      idTokenBody: tokens.idToken.claims,
    };
  }

  setDeepLink() {
    this._okta.setOriginalUri(
      this._window.location.pathname + this._window.location.search,
    );
  }

  private handleHttpError(e: AuthApiError) {
    if (e.xhr instanceof TypeError) {
      return throwError({ errorCode: 'ERROR000' });
    }

    return throwError(e);
  }
}

function getOrigin(url: string): string {
  return new URL(url).origin;
}

function checkClaims(claims: unknown): claims is TokenBody {
  return true;
}

function tokenToOidc(token: string): OidcResult {
  const accessTokenBody = jwt_decode(token);
  return {
    accessToken: token,
    accessTokenBody,
    idTokenBody: {},
  } as unknown as OidcResult;
}
